import type { FC } from 'react'
import type { ProjectResult, ProjectSummary } from '@/types'

import { ArrowUpRight } from 'lucide-react'
import { toPlatform } from '@/lib/platforms'
import { PageLink } from '@/components/kit/page-link'
import { Photo } from '@/components/kit/photo'
import { PlatformIcon } from '@/components/kit/platform-icon'

export const formatResult = (result: ProjectResult): string => {
    const number = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: result.decimals ?? 0,
        maximumFractionDigits: result.decimals ?? 0
    }).format(result.value)

    return `${result.prefix ?? ''}${number}${result.suffix ?? ''}`
}

interface ProjectCardProps {
    project: ProjectSummary
    tall?: boolean
}

export const ProjectCard: FC<ProjectCardProps> = ({ project, tall = false }) => {
    return (
        <article className='group relative'>
            <PageLink
                href={`/work/${project.slug}`}
                data-cursor='Open'
                className='block focus-visible:outline-offset-8'
            >
                <div className={`relative overflow-hidden rounded-[2rem] bg-sand ${tall ? 'aspect-[4/5]' : 'aspect-[4/4.4]'}`}>
                    {project.cover && (
                        <Photo
                            src={project.cover}
                            alt={project.cover_alt ?? ''}
                            sizes='(min-width: 768px) 45vw, 92vw'
                            grain
                            className='absolute inset-0'
                            imgClassName='transition-transform duration-[1.2s] ease-expo group-hover:scale-105'
                        />
                    )}

                    <div className='absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-black/15' />

                    <div className='absolute inset-x-0 top-0 z-10 flex items-center justify-between gap-3 p-5'>
                        <span
                            className='rounded-full bg-cream/90 px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.14em] text-ink backdrop-blur'
                            data-mdlfy-cms={`projects:${project.id}:industry`}
                        >
                            {project.industry}
                        </span>

                        <span className='flex gap-1.5'>
                            {project.platforms.map(label => {
                                const platform = toPlatform(label)

                                return platform ? (
                                    <span
                                        key={label}
                                        className='flex size-8 items-center justify-center rounded-full bg-black/30 text-white backdrop-blur-md'
                                    >
                                        <PlatformIcon
                                            name={platform}
                                            className='size-3.5'
                                        />
                                        <span className='sr-only'>{label}</span>
                                    </span>
                                ) : null
                            })}
                        </span>
                    </div>

                    {project.results.length > 0 && (
                        <div className='absolute inset-x-3 bottom-3 z-10 translate-y-[calc(100%_+_1rem)] rounded-[1.5rem] bg-paper/95 p-5 backdrop-blur-md transition-transform duration-700 ease-expo group-hover:translate-y-0 group-focus-within:translate-y-0'>
                            <p className='font-mono text-[10px] uppercase tracking-[0.16em] text-stone'>The read</p>
                            <dl className='mt-3 grid grid-cols-3 gap-3'>
                                {project.results.slice(0, 3).map(result => (
                                    <div
                                        key={result.label}
                                        className='flex flex-col'
                                    >
                                        <dt className='order-2 mt-1 text-[11px] leading-tight text-stone'>{result.label}</dt>
                                        <dd className='font-heading text-2xl font-bold tracking-[-0.035em] text-ink'>{formatResult(result)}</dd>
                                    </div>
                                ))}
                            </dl>
                        </div>
                    )}
                </div>

                <div className='mt-5 flex items-start justify-between gap-6'>
                    <div className='min-w-0'>
                        <h2
                            className='text-[clamp(1.9rem,3vw,2.6rem)] font-bold leading-none tracking-[-0.035em]'
                        >
                            <span data-mdlfy-cms={`projects:${project.id}:brand`}>{project.brand}</span>
                        </h2>
                        {project.tagline && (
                            <p
                                className='mt-2 max-w-md text-[15px] leading-relaxed text-stone'
                                data-mdlfy-cms={`projects:${project.id}:tagline`}
                            >
                                {project.tagline}
                            </p>
                        )}
                    </div>

                    <span className='flex size-12 shrink-0 items-center justify-center rounded-full border border-ink/15 transition-[transform,background-color,border-color] duration-500 ease-expo group-hover:rotate-45 group-hover:border-ember group-hover:bg-ember'>
                        <ArrowUpRight className='size-5' />
                    </span>
                </div>

                {project.headline_result && (
                    <p className='mt-4 inline-flex items-center gap-2 rounded-full bg-ink py-1.5 pl-2 pr-4 text-sm font-medium text-cream'>
                        <span className='size-2 rounded-full bg-ember' />
                        <span data-mdlfy-cms={`projects:${project.id}:headline_result`}>{project.headline_result}</span>
                    </p>
                )}
            </PageLink>
        </article>
    )
}
